import { useTranslation } from "react-i18next";
import { Order } from "@beeldit/ecommerce/orders/models/Order";
import { Badge, Button } from "react-bootstrap";
import OrderLineDetails from "@beeldit/ecommerce/orders/components/OrderLineDetails";
import { Link } from "react-router-dom";
import React, { useContext } from "react";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";

interface Prop {
  order: Order;
  prepareChangeStatusFunction: any;
  downloadShipmentLabelFunction: any;
  prepareUploadInvoiceFunction: any;
  downloadInvoiceFunction: any;
  superOrder?: any;
}

function OrderDetails(props: Prop) {
  const {
    order,
    prepareChangeStatusFunction,
    downloadShipmentLabelFunction,
    prepareUploadInvoiceFunction,
    downloadInvoiceFunction,
    superOrder,
  } = props;

  const { t } = useTranslation();
  const { user } = useContext(AuthContext);

  const changeStatus = () => {
    prepareChangeStatusFunction(order.id, order.status + 1);
  };

  const downloadShipmentLabel = () => {
    downloadShipmentLabelFunction(order);
  };

  const uploadInvoice = () => {
    prepareUploadInvoiceFunction(order.id);
  };

  const downloadInvoice = () => {
    downloadInvoiceFunction(order);
  };

  return (
    <>
      <div className="order">
        <div className="order-heading">
          <div className="order-data">
            <h3 className="order-ref">
              <span className="me-2">
                {t("reference")}: {order?.reference}
              </span>
              <Badge bg="info">{t("order_status_" + order?.status)}</Badge>
            </h3>
            {order.shipment && order.shipment.tracking_url && (
              <div className="order-tracking">
                {t("tracking_url")}:{" "}
                <a
                  href={order.shipment.tracking_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {order.shipment.tracking_url}
                </a>
              </div>
            )}
          </div>
          {order.super_order && (
            <div className="client-data">
              {order.super_order.customer &&
                order.super_order.customer.user && (
                  <div className="mb-4">
                    <h5 className="data-label">{t("customer")}</h5>
                    <ul className="list-unstyled">
                      <li>
                        {order.super_order?.customer.user.name}{" "}
                        {order.super_order?.customer.user.surnames}
                      </li>
                      <li>
                        <Link
                          className="mailto"
                          to={`mailto:${order.super_order?.customer.user.email}`}
                        >
                          <small>
                            {order.super_order?.customer.user.email}
                          </small>
                        </Link>
                      </li>
                    </ul>
                  </div>
                )}
              {order.super_order.shipping_address && (
                <div className="mb-4 pt-4 border-top">
                  <h5 className="data-label">{t("shipping_address")}</h5>
                  <p>
                    {order.super_order.shipping_address.street && (
                      <span>{order.super_order.shipping_address.street}</span>
                    )}
                    {order.super_order.shipping_address.postal_code && (
                      <span>
                        , {order.super_order.shipping_address.postal_code}
                      </span>
                    )}
                    {order.super_order.shipping_address.city && (
                      <span> , {order.super_order.shipping_address.city}</span>
                    )}
                    {order.super_order.shipping_address.state && (
                      <span>, {order.super_order.shipping_address.state}</span>
                    )}
                    {order.super_order.shipping_address.country && (
                      <span>
                        , {order.super_order.shipping_address.country}
                      </span>
                    )}
                  </p>
                </div>
              )}
              {order.super_order.billing_address && (
                <div className="mb-4 pt-4 border-top">
                  <h5 className="data-label">{t("billing_address")}</h5>
                  <p>
                    {order.super_order.billing_address.fiscal_address && (
                      <span>
                        {order.super_order.billing_address.fiscal_address}
                      </span>
                    )}
                    {order.super_order.billing_address.fiscal_zip && (
                      <span>
                        {" "}
                        , {order.super_order.billing_address.fiscal_zip}
                      </span>
                    )}
                    {order.super_order.billing_address.fiscal_city && (
                      <span>
                        , {order.super_order.billing_address.fiscal_city}
                      </span>
                    )}
                    {order.super_order.billing_address.fiscal_province && (
                      <span>
                        , {order.super_order.billing_address.fiscal_province}
                      </span>
                    )}
                    {order.super_order.billing_address.fiscal_country_code && (
                      <span>
                        ,{" "}
                        {order.super_order.billing_address.fiscal_country_code}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="order-actions">
            {order.status !== Order.PENDING_STATUS &&
              order.status !== Order.DELIVERED && (
                <Button variant="secondary" onClick={changeStatus}>
                  {t("change_status")}{" "}
                  {t("order_status_" + (order?.status + 1))}
                </Button>
              )}
            {order.shipment && order.shipment.label && (
              <Button
                variant="primary"
                className="me-2"
                onClick={downloadShipmentLabel}
              >
                {t("download_label")}
              </Button>
            )}
            {order.status === Order.DELIVERED &&
              (superOrder
                ? superOrder.request_invoice
                : order.super_order?.request_invoice) &&
              !order.invoice_path &&
              prepareUploadInvoiceFunction && (
                <Button
                  variant="primary"
                  className="me-2"
                  onClick={uploadInvoice}
                >
                  {t("upload_invoice")}
                </Button>
              )}
            {order.status === Order.DELIVERED && order.invoice_path && (
              <Button
                variant="primary"
                className="me-2"
                onClick={downloadInvoice}
              >
                {t("download_invoice")}
              </Button>
            )}
          </div>
        </div>
        <div className="order-content">
          <table className="table order-table">
            <thead>
              <tr>
                <th>{t("product")}</th>
                <th>{t("producer")}</th>
                <th className="text-end">{t("quantity")}</th>
                <th className="text-end">{t("subtotal")}</th>
                <th className="text-end">{t("discount")}</th>
                <th className="text-end">{t("tax")}</th>
                <th className="text-end">{t("total")}</th>
                {user?.role === "admin" && <th className="text-end">{t("commission_amount")}</th>}
              </tr>
            </thead>
            <tbody>
              {order.order_lines.map((orderLine) => (
                <OrderLineDetails
                  key={"order-line-" + orderLine.id}
                  orderLine={orderLine}
                  showMarketplaceCommission={user?.role === "admin"}
                />
              ))}
              {(Number(order.shipping_amount) > 0 || Number(order.shipping_tax) > 0) && (
                <tr>
                  <td className="fw-bold">{t("shipping")}</td>
                  <td></td>
                  <td className="qty"></td>
                  <td className="currency">
                    {new Intl.NumberFormat("es-ES", {
                      style: "currency",
                      currency: "EUR",
                    }).format(Number(order.shipping_amount) || 0)}
                  </td>
                  <td className="currency"></td>
                  <td className="currency">
                    {new Intl.NumberFormat("es-ES", {
                      style: "currency",
                      currency: "EUR",
                    }).format(Number(order.shipping_tax) || 0)}
                  </td>
                  <td className="currency">
                    {new Intl.NumberFormat("es-ES", {
                      style: "currency",
                      currency: "EUR",
                    }).format(
                      (Number(order.shipping_amount) || 0) +
                        (Number(order.shipping_tax) || 0)
                    )}
                  </td>
                  {user?.role === "admin" && <td className="currency"></td>}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default OrderDetails;
